import api from './api';

export interface Notification {
  id: number;
  recipient_name: string;
  recipient_phone: string;
  notification_type: 'sms' | 'whatsapp' | 'email';
  notification_type_display: string;
  message_type: 'payment_reminder' | 'payment_due' | 'overdue_payment' | 'advertisement' | 'general';
  message_type_display: string;
  message_content: string;
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | 'cancelled';
  status_display: string;
  scheduled_at?: string;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  error_message?: string;
  retry_count: number;
  sms_count: number;
  student_name?: string;
  company_name: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationTemplate {
  id: number;
  name: string;
  template_type: string;
  template_type_display: string;
  content: string;
  is_active: boolean;
  available_variables: string[];
  company?: number;
  company_name?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationSchedule {
  id: number;
  name: string;
  schedule_type: 'payment_reminder' | 'payment_due' | 'overdue_payment';
  schedule_type_display: string;
  sms_enabled: boolean;
  sms_days_before: number;
  sms_template?: number;
  sms_template_name?: string;
  whatsapp_enabled: boolean;
  whatsapp_days_before: number;
  whatsapp_template?: number;
  whatsapp_template_name?: string;
  company: number;
  company_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationStats {
  total_notifications: number;
  recent_notifications: number;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  by_message_type: Record<string, number>;
  success_rate: number;
}

export interface CreateNotificationData {
  recipient_name: string;
  recipient_phone: string;
  notification_type: 'sms' | 'whatsapp' | 'email';
  message_type: 'payment_reminder' | 'payment_due' | 'overdue_payment' | 'advertisement' | 'general';
  message_content: string;
  scheduled_at?: string;
  student?: number;
  payment?: number;
  company?: number;
}

export interface NotificationFilters {
  search?: string;
  notification_type?: string;
  message_type?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

class NotificationAPI {
  // Bildirişlər
  async getNotifications(filters: NotificationFilters = {}) {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await api.get(`/notifications/?${params.toString()}`);
    return response.data;
  }

  async getNotification(id: number) {
    const response = await api.get(`/notifications/${id}/`);
    return response.data;
  }

  async createNotification(data: CreateNotificationData) {
    const response = await api.post('/notifications/', data);
    return response.data;
  }

  async updateNotification(id: number, data: Partial<CreateNotificationData>) {
    const response = await api.patch(`/notifications/${id}/`, data);
    return response.data;
  }

  async deleteNotification(id: number) {
    const response = await api.delete(`/notifications/${id}/`);
    return response.data;
  }

  async retryNotification(id: number) {
    const response = await api.post(`/notifications/${id}/retry/`);
    return response.data;
  }

  async sendTestNotification(data: {
    phone_number: string;
    message: string;
    notification_type: 'sms' | 'whatsapp';
  }) {
    const response = await api.post('/notifications/send_test/', data);
    return response.data;
  }

  async getNotificationStatistics() {
    const response = await api.get('/notifications/statistics/');
    return response.data;
  }

  async createPaymentReminders() {
    const response = await api.post('/notifications/create_payment_reminders/');
    return response.data;
  }

  async sendPendingNotifications() {
    const response = await api.post('/notifications/send_pending/');
    return response.data;
  }

  async sendTodayPaymentReminders() {
    const response = await api.post('/notifications/send-today-reminders/');
    return response.data;
  }

  // Şablonlar
  async getTemplates() {
    const response = await api.get('/templates/');
    return response.data;
  }

  async getTemplate(id: number) {
    const response = await api.get(`/templates/${id}/`);
    return response.data;
  }

  async createTemplate(data: Partial<NotificationTemplate>) {
    const response = await api.post('/templates/', data);
    return response.data;
  }

  async updateTemplate(id: number, data: Partial<NotificationTemplate>) {
    const response = await api.patch(`/templates/${id}/`, data);
    return response.data;
  }

  async deleteTemplate(id: number) {
    const response = await api.delete(`/templates/${id}/`);
    return response.data;
  }

  // Planlar
  async getSchedules() {
    const response = await api.get('/schedules/');
    return response.data;
  }

  async getSchedule(id: number) {
    const response = await api.get(`/schedules/${id}/`);
    return response.data;
  }

  async createSchedule(data: Partial<NotificationSchedule>) {
    const response = await api.post('/schedules/', data);
    return response.data;
  }

  async updateSchedule(id: number, data: Partial<NotificationSchedule>) {
    const response = await api.patch(`/schedules/${id}/`, data);
    return response.data;
  }

  async deleteSchedule(id: number) {
    const response = await api.delete(`/schedules/${id}/`);
    return response.data;
  }
}

export const notificationAPI = new NotificationAPI();